import {
  any,
  array,
  bool,
  func,
  number,
  object,
  oneOfType,
  string,
} from 'prop-types'
import { Component } from 'react'
import { Conditional, Loader } from '@opallabs/kuiper'
import { PositionedArrayManager } from '@opallabs/lambda'
import { track } from '@opallabs/quanta'
import { isApproval, isTask } from '../helpers/matchers'
import Content from './Content'
import Footer from './Footer'
import Header from './Header'
import {
  canExpand,
  classNames,
  getItems,
  makeDue,
  newPhaseItem,
  phaseActions,
  shouldRenderFooter,
} from './helpers'

import './styles.scss'

const arrayManager = new PositionedArrayManager()

const buildPhaseActionMap = (props) => ({
  [phaseActions.Delete]: props.remove,
  [phaseActions.MoveDown]: props.moveDown,
  [phaseActions.MoveUp]: props.moveUp,
  [phaseActions.Resend]: props.resendApprovals,
  [phaseActions.Retract]: props.retractApprovals,
})

function trackPhaseAction(actionName, phaseData) {
  const trackingData = {
    contentId: phaseData.postId,
    phaseId: parseInt(phaseData.id, 10),
    phaseType: phaseData.phase_type,
  }

  if (actionName === phaseActions.Delete) {
    track('Workflow: Phase Delete', trackingData)
  } else if (
    actionName === phaseActions.MoveDown ||
    actionName === phaseActions.MoveUp
  ) {
    track('Workflow: Phase Move', trackingData)
  }
}

export default class Phase extends Component {
  static propTypes = {
    className: any,
    createPhaseItem: func.isRequired,
    currentUser: object,
    id: oneOfType([number, string]),
    is_complete: bool,
    isAfterActivePhase: bool,
    isFinalApproval: bool,
    isLimitedRole: bool,
    isWorkflowTemplate: bool,
    loading: bool,
    phase_type: string,
    placeholder: string,
    requires_all_approvals: bool,
    tasks: array,
    theme: string,
    workflowLocation: string,
  }

  static defaultProps = {
    isFinalApproval: false,
    isWorkflowTemplate: false,
    loading: false,
  }

  constructor(props) {
    super(props)

    const items = getItems(props)
    this.state = {
      isExpanded: false,
      items,
      placeholders: [],
    }
  }

  componentDidMount() {
    this.phaseActionMap = buildPhaseActionMap(this.props)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState((prevState) => {
      const items = getItems(nextProps)

      // If a new "real" item has been added, we need to remove one placeholder
      const placeholders =
        items.length > prevState.items.length
          ? prevState.placeholders.slice(0, -1)
          : prevState.placeholders

      // Empty/new Approvals Phases need to have at least one placeholder
      const combinedItems = items.concat(placeholders)
      if (isApproval(nextProps.phase_type) && !combinedItems.length) {
        placeholders.push(newPhaseItem(arrayManager, items))
      }

      return {
        items,
        placeholders,
      }
    })
  }

  componentDidUpdate(nextProps) {
    this.phaseActionMap = buildPhaseActionMap(nextProps)
  }

  /**
   * Creates a new PLACEHOLDER approval; note that Approvals do not become "real"
   * until they are assigned, and are only stored locally until that time.
   */
  addApprovalPlaceholder() {
    this.setState(({ placeholders }) => ({
      placeholders: placeholders.concat({
        isPlaceholder: true,
        uuid: placeholders.length + 1,
      }),
    }))
  }

  /**
   * Creates a new Task for this Phase. Unless otherwise specified, a "placeholder" Task will be placed in
   *    local state while we await a response from the API.
   * @param addPlaceholder - Whether we should add a "placeholder" task to local state while waiting for API response.
   *    We will usually want this behavior except in the case of brand new Phases.
   */
  createNewTask = (addPlaceholder = true) => {
    if (!this.props.id) {
      // ignore placeholder phases
      return
    }
    track('Task: Create')

    const phaseData = { id: this.props.id }
    const newTask = newPhaseItem(arrayManager, this.state.items)
    const updateData = { request: 'Task', type: 'tasks' }
    const tempTaskForState = { ...newTask, request: 'Task', focusOnMount: true }

    this.setState(
      (prevState) => ({
        items: addPlaceholder
          ? prevState.items.concat(tempTaskForState)
          : prevState.items,
      }),
      () => this.props.createPhaseItem(phaseData, newTask, updateData)
    )
  }

  addItem = () => {
    if (isApproval(this.props.phase_type)) {
      this.addApprovalPlaceholder()
    } else {
      this.createNewTask()
    }
  }

  expandPhase = () => {
    if (canExpand(this.props)) {
      this.setState({ isExpanded: true })
    }
  }

  tabIndex = () => {
    if (canExpand(this.props)) {
      return 0
    }
    return undefined
  }

  toggleExpanded = (e) => {
    e.stopPropagation()
    if (canExpand(this.props)) {
      this.setState((prevState) => ({
        isExpanded: !prevState.isExpanded,
      }))
    }
  }

  handleKeyUp = (e) => {
    if ([' ', 'Enter'].includes(e.key)) {
      e.preventDefault()
      e.stopPropagation()

      this.toggleExpanded(e)
    }
  }

  onContextMenuAction = (data) => {
    const clickedAction = data.action
    if (clickedAction in this.phaseActionMap) {
      const phaseData = this.props
      trackPhaseAction(clickedAction, phaseData)
      this.phaseActionMap[clickedAction](phaseData)
    }
  }

  get otherProps() {
    const {
      /* Object spread is being used to filter out props here */
      /* eslint-disable no-unused-vars */
      className,
      currentUser,
      isWorkflowTemplate,
      loading,
      /* eslint-enable no-unused-vars */
      ...otherProps
    } = this.props
    return otherProps
  }

  onPhaseItemsChanged = (items) => {
    // [QA-26] when ListManager updates its internal items, we need to make sure our local placeholder items are in sync
    const placeholderCount = items.reduce(
      (acc, item) => (item.isPlaceholder ? acc + 1 : acc),
      0
    )
    this.setState(({ placeholders }) => ({
      placeholders:
        placeholders.length > placeholderCount
          ? placeholders.slice(0, placeholderCount)
          : placeholders,
    }))
  }

  render() {
    const { isExpanded } = this.state
    const {
      is_complete: isComplete,
      isLimitedRole,
      isWorkflowTemplate,
      requires_all_approvals: requiresAllApprovals,
      workflowLocation,
    } = this.props

    const otherProps = this.otherProps
    const {
      approvals,
      disabled,
      due_date,
      is_active,
      is_current,
      isFinalApproval,
      locked,
      name,
      phase_type: phaseType = '',
      tasks,
      update,
    } = otherProps

    const dueDate = makeDue(due_date)
    const placeholderPlacement = locked ? 'none' : 'bottom'
    const phaseDetails = {
      approvals,
      is_active,
      is_complete: isComplete,
      is_current,
      isFinalApproval,
      phase_type: phaseType,
      tasks,
    }
    const combinedItems = this.state.items.concat(this.state.placeholders)
    const showFooter = shouldRenderFooter(isComplete, workflowLocation)
    // Task Phases with no 'items' (i.e. newly-created Phases) should show loading state until they get a real item
    const forceLoadingState = isTask(phaseType) && !combinedItems.length
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div
        className={classNames({
          className: this.props.className,
          disabled,
          expanded: isExpanded,
          phase: phaseDetails,
          theme: this.props.theme,
        })}
        onClick={this.expandPhase}
        onKeyUp={this.handleKeyUp}
        tabIndex={this.tabIndex()}
      >
        <Header
          disabled={disabled}
          dueDate={dueDate}
          hideDate={isWorkflowTemplate}
          isComplete={isComplete}
          isExpanded={isExpanded}
          isFinalApproval={isFinalApproval}
          onClickHeader={this.toggleExpanded}
          phaseName={name}
          phaseType={phaseType}
          theme={this.props.theme}
          updatePhase={update}
          workflowLocation={workflowLocation}
        />

        <Content
          createNewTask={isTask(phaseType) ? this.createNewTask : undefined}
          currentUser={this.props.currentUser}
          dueDate={dueDate}
          isExpanded={isExpanded}
          isLimitedRole={isLimitedRole}
          onPhaseItemsChanged={this.onPhaseItemsChanged}
          phaseItems={combinedItems}
          placeholderPlacement={placeholderPlacement}
          requiresAllApprovals={requiresAllApprovals}
          theme={this.props.theme}
          workflowLocation={workflowLocation}
          {...otherProps}
        />

        <Conditional condition={showFooter}>
          <Footer
            addItem={this.addItem}
            onContextMenuAction={this.onContextMenuAction}
            phaseType={phaseType}
            theme={this.props.theme}
            {...otherProps}
          />
        </Conditional>

        <Loader
          loaded={!this.props.loading && !forceLoadingState}
          theme="white"
        />
      </div>
    )
    /* eslint-enable jsx-a11y/no-static-element-interactions */
  }
}
